import pako from 'pako'
import router from '@builder/router/index'
import {
  getStylesBySid,
  getCssForSids,
  insertSectionStyle,
  collectSids,
  filterFontFacesByUsage,
  applyPageStyles,
  combineStyles
} from '@/utils/useStylesheet'

export const PUSH_PAGE = 'pushPage'
export const REDIRECT_URL = 'redirectURL'
export const MODULE = 'openModule'

const itemTypes = {
  page: PUSH_PAGE,
  custom_url: REDIRECT_URL,
  custom: REDIRECT_URL,
  'module': MODULE
}

const state = () => ({
  pages: [],
  page: {},
  currentPage: null,
  routeItem: {},
  loading: false
})

const getters = {
  getPages (state) {
    return state.pages
  }
}

const actions = {
  async saveOld ({state, rootState, dispatch, rootGetters}, payload = {clearCache: 0, all: 0, force: false}) {
    const draft = JSON.stringify(rootGetters['sections/getSections'])
    if (state.loading || (!rootGetters['sections/getSections'].length && !payload.force)) return

    state.loading = true
    VEvent.fire('page.save')

    try {
      const fonts = await dispatch('font/prepareFonts', {}, {root: true})

      return await axios.put(`api/projects/${rootState.projects.project.id}/pages/${state.page.id}?autoSave=1&all=${payload.all ? 1 : 0}${payload.clearCache ? '&clearCache=1' : ''}`, {
        meta_keywords: state.page.meta_keywords,
        meta_title: state.page.meta_title,
        meta_description: state.page.meta_description,
        meta_index: state.page.meta_index,
        slug: state.page.slug,
        title: state.page.title,
        content: state.page.content,
        hideHeader: state.page.hideHeader,
        hideFooter: state.page.hideFooter,
        force_reload: state.page.force_reload,
        password_id: state.page.password_id,
        settings: JSON.stringify(state.page.settings),
        code_injection: JSON.stringify(state.page.code_injection),
        content_draft: draft,
        open_graph_image: state.page.open_graph_image,
        fonts: fonts.googleFonts + fonts.customFonts,
        canonical_url: state.page.canonical_url
      })
    } catch (err) {
      this.$swal({
        icon: 'error',
        title: 'Server error',
        text: 'Please try again later or let us know about your problem.',
        showConfirmButton: false,
        timer: 3500
      })
      throw err
    } finally {
      state.loading = false
    }
  },
  async save ({state, rootState, dispatch, rootGetters, commit}, payload = {clearCache: 0, all: 0, force: false, live: false}) {
    const sections = rootGetters['sections/getSections']
    const navbar = rootState.editor.globalStyles.navbar.block
    const footer = rootState.editor.globalStyles.footer.block
    const draft = JSON.stringify(sections)
    if (state.loading || (!sections.length && !payload.force)) return
    state.loading = true
    VEvent.fire('page.save')
    const sectionSids = collectSids(sections)
    const navbarSids = collectSids(navbar)
    const footerSids = collectSids(footer)
    const pageSids = [...sectionSids, ...navbarSids, ...footerSids]
    const extraSids = [...navbarSids, ...footerSids]
    const css = getCssForSids(sectionSids)
    const pageCss = getCssForSids(pageSids)
    const extraCss = getCssForSids(extraSids)

    const defaultCSS = '@media screen{.el-menu-link:hover{}.el-menu-link{}.el-links-wrapper{}.link-active{}.flex-col-inner{}.col-hover-v2{}.flex-wrapper{}.el-flex-wrapper{}.el-container{}.el-block{}.estage-btn{}.estage-btn:hover{}}@media (max-width:1100px){.el-menu-link:hover{}.el-menu-link{}.el-links-wrapper{}.link-active{}.flex-col-inner{}.col-hover-v2{}.flex-wrapper{}.el-flex-wrapper{}.el-container{}.el-block{}.estage-btn{}.estage-btn:hover{}}@media (max-width:520px){.el-menu-link:hover{}.el-menu-link{}.el-links-wrapper{}.link-active{}.flex-col-inner{}.col-hover-v2{}.flex-wrapper{}.el-flex-wrapper{}.el-container{}.el-block{}.estage-btn{}.estage-btn:hover{}}'

    try {
      if (extraCss.length > 0 && extraCss !== defaultCSS) {
        const {data} = await axios.put(`api/projects/${rootState.projects.project.id}/css`, {
          global_css: payload.live ? extraCss : undefined,
          global_css_draft: extraCss
        })

        if (data.status === 'success') {
          commit('projects/SET_GLOBAL_CSS', data.data.global_css, {root: true})
          commit('projects/SET_GLOBAL_CSS_DRAFT', data.data.global_css_draft, {root: true})
        }
      }

      const fonts = await dispatch('font/prepareFonts', {}, {root: true})
      const fontsCss = fonts.googleFonts + fonts.customFonts
      const { keptFontFaces } = filterFontFacesByUsage(pageCss, fontsCss)

      const outputCSS = css.length > 0 && css !== defaultCSS ? css : undefined

      const pagePayload = {
        meta_keywords: state.page.meta_keywords,
        meta_title: state.page.meta_title,
        meta_description: state.page.meta_description,
        meta_index: state.page.meta_index,
        slug: state.page.slug,
        title: state.page.title,
        content: state.page.content,
        hideHeader: state.page.hideHeader,
        hideFooter: state.page.hideFooter,
        force_reload: state.page.force_reload,
        password_id: state.page.password_id,
        settings: JSON.stringify(state.page.settings),
        code_injection: JSON.stringify(state.page.code_injection),
        content_draft: draft,
        open_graph_image: state.page.open_graph_image,
        fonts: keptFontFaces,
        canonical_url: state.page.canonical_url,
        css: payload.live ? outputCSS : undefined,
        css_draft: outputCSS
      }
      const pagePayloadStr = JSON.stringify(pagePayload)
      const pagePayloadCompressed = pako.gzip(pagePayloadStr)

      const {data} = await axios.put(
        `api/projects/${rootState.projects.project.id}/pages/${state.page.id}?autoSave=1&all=${payload.all ? 1 : 0}${payload.clearCache ? '&clearCache=1' : ''}`,
        pagePayloadCompressed,
        {
          headers: {
            'Content-Encoding': 'gzip',
            'Content-Type': 'application/json'
          }
        }
      )

      if (data.data) {
        state.page.css_file = data.data?.css_file
        state.page.css_file_draft = data.data?.css_file_draft
        state.page.page_status = data.data?.page_status
        await dispatch('backup')
      }
    } catch (err) {
      this.$swal.fire({
        icon: 'error',
        title: 'Server error',
        text: 'Please try again later or let us know about your problem.',
        showConfirmButton: false,
        timer: 3500
      })
      throw err
    } finally {
      state.loading = false
    }
  },
  resetPages ({state}) {
    state.pages = []
  },
  async addPage ({rootState, dispatch}, payload) {
    return await axios.post(`api/projects/${rootState.projects.project.id}/pages`, payload)
      .then(response => {
        if (payload.dont_dispatch === undefined) dispatch('loadPages')
        return response.data.data
      })
  },
  async editPage ({state, rootState}, payload) {
    if (payload.id === state.page.id) {
      state.page.meta_title = payload.meta_title
    }

    return await axios.put(`api/projects/${rootState.projects.project.id}/pages/${payload.id}`, payload)
      .then((data) => {
        return data.data
      })
  },
  async deletePage ({rootState, dispatch}, payload) {
    return await axios.delete(`api/projects/${rootState.projects.project.id}/pages/${payload.id}`, payload)
      .then(() => {
        dispatch('menu/loadNavbarMenu', null, {root: true})
      })
  },
  async loadHome ({state, rootState, dispatch, commit}) {
    return await axios.get(`api/site/${rootState.projects.project.subdomain}/home-page?builder=1&_t=${Date.now()}`)
      .then(async ({data}) => {
        if (!data.data) {
          return await dispatch('projects/gettingReady', {data: rootState.projects.project}, {root: true})
        }

        commit('editor/SET_TABLET', false, {root: true})
        commit('editor/SET_RESOLUTION', 'lg', {root: true})

        let css = ''
        let extraCss = ''
        const defaultCSS = '@media screen{.el-menu-link{}.flex-col-inner{}.flex-wrapper{}.el-container{}.el-links-wrapper{font-family:work sans}.estage-btn{background-color:#ac1414;color:#ebda2c;letter-spacing:2px;font-family:Poppins}.estage-btn:hover{color:#ffd451}}@media(max-width:1100px){.el-menu-link{}.link-active{}.col-hover-v2{}.el-flex-wrapper{}.el-block{}.estage-btn:hover{}}@media(max-width:520px){.el-menu-link{}.link-active{}.col-hover-v2{}.el-flex-wrapper{}.el-block{}.estage-btn:hover{}}'

        if (data.data.css_file_draft !== null) {
          css = await fetch(data.data.css_file_draft).then(res => res.text())

          if (css === defaultCSS) {
            const url = rootState.projects.project.css_file_draft !== null
              ? rootState.projects.project.css_file_draft
              : rootState.projects.project.css_file

            if (url) css = await fetch(url).then(res => res.text())
          }
        } else {
          const url = rootState.projects.project.css_file_draft !== null
            ? rootState.projects.project.css_file_draft
            : rootState.projects.project.css_file

          if (url) css = await fetch(url).then(res => res.text())
        }

        // If we accidentally got a full HTML page instead of plain CSS
        if (css.includes('<!DOCTYPE html>')) {
          const parser = new DOMParser()
          const doc = parser.parseFromString(css, 'text/html')
          const styleEl = doc.getElementById('sty')

          // Take only the styles from <style id="sty">
          css = styleEl ? (styleEl.textContent || styleEl.innerHTML) : ''
        }

        if (rootState.projects.project.global_css_draft) {
          extraCss = await fetch(rootState.projects.project.global_css_draft).then(res => res.text())
        }

        const outputCss = combineStyles(css, extraCss)

        applyPageStyles(outputCss)

        state.page = {
          ...data.data,
          settings: data.data.settings ? JSON.parse(data.data.settings) : {},
          code_injection: JSON.parse(data.data.code_injection || '{}')
        }

        rootState.sections.sections = JSON.parse(data.data.content_draft)

        router.replace({
          query: {
            page: data.data.id
          }
        }).catch(err => {
          if (err.name !== 'NavigationDuplicated') {
            throw err
          }
        })
      })
  },
  async loadPage ({state, commit, rootState}, id, refresh = false) {
    commit('promoBar/CLEAR', null, {root: true})
    state.loading = true

    return await axios.get(`api/projects/${rootState.projects.project.id}/pages/${id}`)
      .then(async ({data}) => {
        commit('editor/SET_TABLET', false, {root: true})
        commit('editor/SET_RESOLUTION', 'lg', {root: true})

        let css = ''
        let extraCss = ''
        const defaultCSS = '@media screen{.el-menu-link{}.flex-col-inner{}.flex-wrapper{}.el-container{}.el-links-wrapper{font-family:work sans}.estage-btn{background-color:#ac1414;color:#ebda2c;letter-spacing:2px;font-family:Poppins}.estage-btn:hover{color:#ffd451}}@media(max-width:1100px){.el-menu-link{}.link-active{}.col-hover-v2{}.el-flex-wrapper{}.el-block{}.estage-btn:hover{}}@media(max-width:520px){.el-menu-link{}.link-active{}.col-hover-v2{}.el-flex-wrapper{}.el-block{}.estage-btn:hover{}}'

        if (data.data.css_file_draft !== null) {
          css = await fetch(data.data.css_file_draft).then(res => res.text())

          if (css === defaultCSS) {
            const url = rootState.projects.project.css_file_draft !== null
              ? rootState.projects.project.css_file_draft
              : rootState.projects.project.css_file

            if (url) css = await fetch(url).then(res => res.text())
          }
        } else {
          const url = rootState.projects.project.css_file_draft !== null
            ? rootState.projects.project.css_file_draft
            : rootState.projects.project.css_file

          if (url) css = await fetch(url).then(res => res.text())
        }

        // If we accidentally got a full HTML page instead of plain CSS
        if (css.includes('<!DOCTYPE html>')) {
          const parser = new DOMParser()
          const doc = parser.parseFromString(css, 'text/html')
          const styleEl = doc.getElementById('sty')

          // Take only the styles from <style id="sty">
          css = styleEl ? (styleEl.textContent || styleEl.innerHTML) : ''
        }

        if (rootState.projects.project.global_css_draft) {
          extraCss = await fetch(rootState.projects.project.global_css_draft).then(res => res.text())
        }

        const outputCss = combineStyles(css, extraCss)

        applyPageStyles(outputCss)

        const draftContent = data.data.content_draft === 'null' ? [] : JSON.parse(data.data.content_draft)
        state.page = {
          ...data.data,
          content_draft: draftContent,
          settings: data.data.settings ? JSON.parse(data.data.settings) : {},
          code_injection: JSON.parse(data.data.code_injection || '{}')
        }

        if (refresh) {
          commit('sections/SET_SECTIONS', [], {root: true})
        }

        setTimeout(() => {
          commit('sections/SET_SECTIONS', draftContent, {root: true})
          if (rootState.sections.sections.length === 0) {
            commit('editor/SET_PAGE', 'blocks', {root: true})
          }
        })

        router.replace({
          query: {
            page: id
          }
        }).catch(err => {
          if (err.name !== 'NavigationDuplicated') {
            throw err
          }
        })
        return state.page
      })
      .finally(() => {
        state.loading = false
      })
  },
  async duplicatePage ({dispatch, rootState}, {id, css_hash}) {
    VEvent.fire('loader', true)

    return await axios.post(`api/projects/${rootState.projects.project.id}/pages/${id}/duplicate`)
      .then(({data}) => {
        return dispatch('loadPage', data.data.id, true)
          .finally(async () => {
            insertSectionStyle(JSON.parse(JSON.stringify(getStylesBySid([css_hash])).replaceAll(css_hash, data.data.css_hash)))
            await dispatch('projects/save', {}, {root: true})
            VEvent.fire('loader', false)
          })
      })
  },
  async loadPages ({state, rootState}, page = 1) {
    let result = []
    await axios.get(`api/projects/${rootState.projects.project.id}/pages?perPage=50&page=${page}&orderId=${state.page.id ? state.page.id : ''}`)
      .then(({data}) => {
        state.pages = data.data
        result = data.data
      })
    return result
  },
  async backup ({state, rootState, rootGetters}) {
    const draft = JSON.stringify(rootGetters['sections/getSections'])

    await axios.post('/api/backup/pages', {
      original_id: state.page.id,
      slug: state.page.slug,
      title: state.page.title,
      content: state.page.content,
      content_draft: draft,
      settings: JSON.stringify(state.page.settings),
      fonts: state.page.fonts,
      css_hash: state.page.css_hash,
      css_file: state.page.css_file,
      css_file_draft: state.page.css_file_draft,
      hash: state.page.hash,
      original_css: rootState.projects.project.css_file_draft,
      page_status: state.page.page_status,
      project_id: rootState.projects.project.id,
      global_css: rootState.projects.project.global_css,
      global_css_draft: rootState.projects.project.global_css_draft
    })
  },
  async loadPagesScrolling ({rootState}, params = {page: 1}) {
    return await axios.get(`api/projects/${rootState.projects.project.id}/pages`, {
      params: {
        perPage: 25,
        ...params
      }
    })
      .then(({data}) => {
        return data.data
      })
  },
  async [PUSH_PAGE] ({dispatch}, item) {
    VEvent.fire('loader', true)
    try {
      await dispatch('loadPage', item.item_object_id)
    } finally {
      VEvent.fire('loader', false)
    }
  },
  [REDIRECT_URL] (_, item) {
    if (item.open_new_tab || !item.hasOwnProperty('open_new_tab')) {
      return window.open(item.item_url)
    }
    return window.location = item.item_url
  },
  [MODULE] (_, item) {
    if (item.item_url === 'blog') {
      router.push({name: 'blog.manage-posts'})
    }
    // window.open(rootState.projects.project.domain ? addhttp(rootState.projects.project.domain) : `${process.env.VUE_APP_ESTAGE_SITE_HOST}` + `/${rootState.projects.project.subdomain}/${item.item_url}`)
    // window.open(`http://localhost:8081/${rootState.projects.project.subdomain}/${item.item_url}`)
  },
  goToPage ({state, dispatch}, payload) {
    const action = itemTypes[payload.item_type]
    if (action !== MODULE) {
      state.routeItem = payload
    }
    dispatch(action, payload)
  }
}

const mutations = {}

export default {
  namespaced: true,
  getters,
  state,
  actions,
  mutations
}
